import { Fragment, useEffect, useMemo, useRef } from 'react';

import {
  DataTableDetailPanel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TooltipProvider,
  useDataTable
} from 'design-system';

import { TABLE_ROW_HEIGHT_PX } from '@/config/constants';
import { Permission } from '@/entities/permissions';
import { SymbolsPreview } from '@/features/_shared';
import { DateTextVariants, useDateText, usePermission } from '@/features/_shared/user';
import { captureEvent } from '@/services/analytics';

import { getCommoditiesTableColumns, type CommodityTableRow } from '../commoditiesTableColumns';
import { useCommoditiesSelectionAdapter } from '../useCommoditiesSelectionAdapter';

import type { CommoditiesTableSectionType } from '../../../types';

type CommoditiesTableTabProps = {
  sections: CommoditiesTableSectionType[];
  height?: number;
  isLoading?: boolean;
  disableSelection?: boolean;
};

const getMaxHeight = (height: number | undefined) =>
  height ? `${height * TABLE_ROW_HEIGHT_PX + 47 - TABLE_ROW_HEIGHT_PX / 1.5}px` : 'fit-content';

const getAlignmentClass = (align: 'start' | 'center' | 'end' | undefined) => {
  if (align === 'end') return 'text-right';
  if (align === 'center') return 'text-center';
  return undefined;
};

const isActionTarget = (target: EventTarget | null) =>
  target instanceof Element && Boolean(target.closest('button, a, [role="checkbox"]'));

const CommoditiesTableTab = ({
  sections,
  height,
  isLoading = false,
  disableSelection = false
}: CommoditiesTableTabProps) => {
  const { hasPermission } = usePermission();
  const { formatDateString } = useDateText();
  const isMarketRestricted = !hasPermission(Permission.ViewMarketScreens);
  const selectionDisabled = !hasPermission(Permission.ViewCatalogDetails);

  const rows = useMemo<CommodityTableRow[]>(
    () =>
      sections.flatMap<CommodityTableRow>((section, sectionIndex) => {
        const commodityRows = section.rows.map((value, rowIndex) => ({
          kind: 'commodity' as const,
          id: `commodity:${sectionIndex}:${rowIndex}:${value.symbol.key.Code}:${value.diffSymbol.key.Code}`,
          value,
          restricted: isMarketRestricted || (!value.last && !value.diff)
        }));

        return section.name.trim()
          ? [
              {
                kind: 'section' as const,
                id: `section:${sectionIndex}:${section.name}`,
                name: section.name,
                subRows: commodityRows
              }
            ]
          : commodityRows;
      }),
    [isMarketRestricted, sections]
  );

  const flatValues = useMemo(() => sections.flatMap(section => section.rows), [sections]);
  const showLast = useMemo(() => flatValues.some(row => Boolean(row.last)), [flatValues]);
  const showLastChange = useMemo(() => flatValues.some(row => Boolean(row.delta)), [flatValues]);
  const showDiff = useMemo(() => flatValues.some(row => Boolean(row.diff)), [flatValues]);
  const showDiffChange = useMemo(
    () => flatValues.some(row => Boolean(row.diffDelta)),
    [flatValues]
  );

  const selectionAdapter = useCommoditiesSelectionAdapter({
    sections,
    selectionDisabled
  });
  const columns = useMemo(
    () =>
      getCommoditiesTableColumns({
        disableSelection,
        formatDate: date => formatDateString(date, DateTextVariants.DAY_MONTH),
        isLoading,
        selectionAdapter,
        showDiff,
        showDiffChange,
        showLast,
        showLastChange
      }),
    [
      disableSelection,
      formatDateString,
      isLoading,
      selectionAdapter,
      showDiff,
      showDiffChange,
      showLast,
      showLastChange
    ]
  );

  const table = useDataTable({
    columns,
    data: rows,
    getRowCanExpand: row =>
      row.original.kind === 'section' ? row.original.subRows.length > 0 : !row.original.restricted,
    getRowId: row => row.id,
    getSubRows: row => (row.kind === 'section' ? row.subRows : undefined)
  });
  const initializedSectionIds = useRef(new Set<string>());
  useEffect(() => {
    const newSectionIds = rows
      .filter(
        (row): row is Extract<CommodityTableRow, { kind: 'section' }> => row.kind === 'section'
      )
      .map(row => row.id)
      .filter(rowId => !initializedSectionIds.current.has(rowId));
    if (newSectionIds.length === 0) return;
    newSectionIds.forEach(rowId => initializedSectionIds.current.add(rowId));

    table.setExpanded(current => {
      const next = current === true ? {} : { ...current };
      let changed = current === true;
      for (const rowId of newSectionIds) {
        if (next[rowId] === undefined) {
          next[rowId] = true;
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [rows, table]);
  const visibleRows = table.getRowModel().rows;

  return (
    <TooltipProvider>
      <section
        aria-label='Commodities table scroll area'
        className='w-full overflow-auto'
        style={{ maxHeight: getMaxHeight(height) }}
      >
        <Table aria-label='Commodities table'>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableHead
                    key={header.id}
                    className={getAlignmentClass(header.column.columnDef.meta?.align)}
                  >
                    {header.isPlaceholder ? null : <table.FlexRender header={header} />}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {visibleRows.map(row => {
              const commodity = row.original.kind === 'commodity' ? row.original : null;

              return (
                <Fragment key={row.id}>
                  <TableRow
                    className={
                      commodity?.restricted ? 'bg-muted! text-muted-foreground' : undefined
                    }
                    data-tone={commodity?.restricted ? 'restricted' : undefined}
                    onClick={event => {
                      if (!commodity || isActionTarget(event.target)) return;
                      captureEvent('table_row_click', {
                        symbol: commodity.value.symbol,
                        diffSymbol: commodity.value.diffSymbol
                      });
                    }}
                  >
                    {row.getVisibleCells().map(cell => (
                      <TableCell
                        key={cell.id}
                        className={getAlignmentClass(cell.column.columnDef.meta?.align)}
                      >
                        <table.FlexRender cell={cell} />
                      </TableCell>
                    ))}
                  </TableRow>
                  {commodity ? (
                    <DataTableDetailPanel
                      colSpan={row.getVisibleCells().length}
                      row={row}
                    >
                      <div className='commodities-detail-preview [&_[role=columnheader]]:!h-5 [&_[role=columnheader]]:!px-1 [&_[role=columnheader]]:!py-0 [&_[role=columnheader]]:!text-xs [&_[role=cell]]:!h-5 [&_[role=cell]]:!px-1 [&_[role=cell]]:!py-0 [&_[role=cell]]:!text-xs [&_[role=cell]]:truncate'>
                        <SymbolsPreview
                          enableQuery
                          hideInfo={false}
                          items={[
                            {
                              symbol: commodity.value.symbol.key,
                              label: 'Last',
                              metadata: commodity.value.metadata,
                              periodAbs: commodity.value.periodAbs
                            },
                            {
                              symbol: commodity.value.diffSymbol.key,
                              label: 'Diff',
                              metadata: commodity.value.diffMetadata,
                              periodAbs: commodity.value.diffPeriodAbs
                            }
                          ]}
                          presentation='nested'
                          stickyHeader={false}
                        />
                      </div>
                    </DataTableDetailPanel>
                  ) : null}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </section>
    </TooltipProvider>
  );
};

export default CommoditiesTableTab;
